import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiExternalLink } from 'react-icons/fi';
import { useTranslation } from '../hooks/useTranslation';

const PortfolioSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const portfolioItems = [
    {
      id: 1,
      title: 'Corporate Tax Optimization',
      category: 'tax',
      description: 'Reduced tax liability by 35% for a multinational corporation through strategic planning.',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800',
      stats: { savings: '$2.5M', timeline: '6 Months', team: '5 Experts' },
      technologies: ['Tax Strategy', 'Compliance', 'International Tax']
    },
    {
      id: 2,
      title: 'Financial System Overhaul',
      category: 'consulting',
      description: 'Implemented modern accounting systems for a growing tech startup.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800',
      stats: { savings: '40%', timeline: '3 Months', team: '3 Consultants' },
      technologies: ['QuickBooks', 'Cloud Accounting', 'Automation']
    },
    {
      id: 3,
      title: 'Audit & Compliance',
      category: 'audit',
      description: 'Comprehensive financial audit for a publicly traded company.',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800',
      stats: { accuracy: '99.9%', timeline: '4 Months', team: '8 Auditors' },
      technologies: ['GAAP', 'SOX', 'Internal Controls']
    },
    {
      id: 4,
      title: 'Small Business Growth',
      category: 'consulting',
      description: 'Financial consulting leading to 200% revenue growth in 18 months.',
      image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=800',
      stats: { growth: '200%', timeline: '18 Months', team: '2 Advisors' },
      technologies: ['Financial Planning', 'Cash Flow', 'Scaling']
    },
    {
      id: 5,
      title: 'Non-Profit Accounting',
      category: 'bookkeeping',
      description: 'Complete financial management system for a large non-profit organization.',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=800',
      stats: { efficiency: '60%', timeline: '2 Months', team: '4 Accountants' },
      technologies: ['Fund Accounting', 'Grant Management', 'Reporting']
    },
    {
      id: 6,
      title: 'M&A Financial Due Diligence',
      category: 'consulting',
      description: 'Financial analysis and due diligence for a major acquisition.',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800',
      stats: { valuation: '$50M', timeline: '2 Months', team: '6 Analysts' },
      technologies: ['Due Diligence', 'Valuation', 'Integration']
    }
  ];

  const filters = [
    { id: 'all', label: t('portfolio.filters.all') },
    { id: 'tax', label: t('portfolio.filters.tax') },
    { id: 'consulting', label: t('portfolio.filters.consulting') },
    { id: 'audit', label: t('portfolio.filters.audit') },
    { id: 'bookkeeping', label: t('portfolio.filters.bookkeeping') }
  ];

  const filteredItems = portfolioItems.filter(item => 
    activeFilter === 'all' || item.category === activeFilter
  );

  return (
    <section id="portfolio" className="py-20 relative">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-[#A37A00] to-[#8A6800] bg-clip-text text-transparent">
              {t('portfolio.title')}
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t('portfolio.subtitle')}
          </p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {filters.map((filter) => (
            <motion.button
              key={filter.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-4 py-2 rounded-full transition-all ${
                activeFilter === filter.id
                  ? 'bg-gradient-to-r from-[#A37A00] to-[#8A6800] text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {filter.label}
            </motion.button>
          ))}
        </div>

        <div ref={ref} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.4 }}
                whileHover={{ y: -10 }}
                className="group relative"
                onClick={() => setSelectedProject(item)}
              >
                <div className="relative h-96 perspective-1000">
                  <motion.div
                    whileHover={{ rotateY: 10, rotateX: 5 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-lg"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute top-4 right-4 px-3 py-1 bg-[#A37A00] text-white rounded-full text-sm font-semibold">
                        {item.category}
                      </div>
                    </div>

                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                        {item.description}
                      </p>

                      <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="text-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                          <div className="text-[#A37A00] dark:text-amber-400 font-bold">{item.stats.savings}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{t('portfolio.stats.savings')}</div>
                        </div>
                        <div className="text-center p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                          <div className="text-amber-700 dark:text-amber-300 font-bold">{item.stats.timeline}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{t('portfolio.stats.timeline')}</div>
                        </div>
                        <div className="text-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                          <div className="text-amber-700 dark:text-amber-300 font-bold">{item.stats.team}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{t('portfolio.stats.team')}</div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {item.technologies.map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="absolute inset-0 bg-gradient-to-t from-[#A37A00]/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="text-white text-center p-6">
                        <div className="mb-4">
                          <FiExternalLink size={32} className="mx-auto" />
                        </div>
                        <div className="text-lg font-semibold">{t('portfolio.caseStudy')}</div>
                        <div className="text-sm">{t('portfolio.clickToLearn')}</div>
                      </div>
                    </div>
                  </motion.div>

                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-amber-700/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 -z-10"></div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center mt-16"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-gradient-to-r from-[#A37A00] to-[#8A6800] text-white rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow"
          >
            {t('portfolio.viewAll')}
          </motion.button>
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-8">
                <h3 className="text-3xl font-bold mb-4 text-[#A37A00]">{selectedProject.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{selectedProject.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PortfolioSection;